import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronRight, Gem, Loader2, X } from 'lucide-react';
import { Button, ClubIcon } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useGetClubsAvailable, useGetMyClub } from '@/services/queries/club';
import { useGetMyCompany } from '@/services/queries/company';
import { useJoinClub } from '@/services/mutations/club';
import { buildCheckoutRedirectUrl } from '@/utils/checkoutRedirectOrigin';
import { userHomeDescriptionAccentLinkClassName } from './userHomeCardStyles';

const sortBenefitsByPriority = (benefits?: any[]) =>
	[...(benefits ?? [])].sort(
		(a, b) => (b?.display_order ?? 0) - (a?.display_order ?? 0),
	);

const getPlansGridColsClass = (count: number) => {
	if (count <= 1) return 'lg:grid-cols-1';
	if (count === 2) return 'lg:grid-cols-2';
	if (count === 3) return 'lg:grid-cols-3';
	return 'lg:grid-cols-4';
};

type JoinClubConfirmModalProps = {
	isOpen: boolean;
	clubName?: string;
	isPending: boolean;
	onClose: () => void;
	onConfirm: () => void;
};

function JoinClubConfirmModal({
	isOpen,
	clubName,
	isPending,
	onClose,
	onConfirm,
}: JoinClubConfirmModalProps) {
	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={isPending ? undefined : onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
					<h2 className="text-lg font-black text-stone-900">
						Confirmar assinatura
					</h2>
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="text-stone-400 transition-colors hover:text-stone-600 disabled:opacity-50"
					>
						<X size={20} />
					</button>
				</div>
				<div className="flex-1 space-y-4 overflow-y-auto p-8 text-sm text-stone-600">
					<p className="font-semibold text-stone-900">
						Você está prestes a assinar o clube {clubName}.
					</p>
					<p>
						Ao continuar, você será redirecionado para um ambiente
						seguro de pagamento para concluir sua assinatura.
					</p>
					<p className="rounded-xl border border-stone-100 bg-stone-50 px-4 py-3 text-xs leading-relaxed text-stone-500">
						Lembrete: os créditos do clube valem apenas dentro do
						mês da assinatura, não acumulam para o mês seguinte e
						não são convertidos em dinheiro. Se ficou com alguma
						dúvida, vale reler a{' '}
						<a
							href="/termos-de-uso"
							target="_blank"
							rel="noopener noreferrer"
							className="font-semibold text-stone-700 underline underline-offset-2 hover:text-stone-900"
						>
							Cláusula 8 dos Termos de Uso
						</a>
						.
					</p>
				</div>
				<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
					>
						Voltar
					</button>
					<Button
						type="button"
						text={isPending ? 'Processando...' : 'Assinar'}
						disabled={isPending}
						onClick={onConfirm}
						className="rounded-full bg-black px-6 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
						startIcon={
							isPending ? (
								<Loader2 className="size-4 animate-spin" />
							) : (
								<Check className="size-4 stroke-[3]" />
							)
						}
					/>
				</div>
			</div>
		</div>,
		document.body,
	);
}

const UserHomePlansSection = () => {
	const navigate = useNavigate();
	const { data: myClub, isLoading: isLoadingMyClub } = useGetMyClub();
	const { data: myCompany, isLoading: isLoadingMyCompany } =
		useGetMyCompany();
	const [joinClubToConfirm, setJoinClubToConfirm] = useState<any | null>(
		null,
	);

	const isInCompany = !!myCompany;
	const hasCompanyClubs = (myCompany?.company_club?.length ?? 0) > 0;
	const activeCompanyPlan = (myCompany?.company_plan_subscription ?? []).find(
		(sub) => sub.status === 'ACTIVE',
	);
	const hasActiveCompanyPlan = !!activeCompanyPlan;
	const shouldDisableJoinForEmployee =
		isInCompany && hasCompanyClubs && !hasActiveCompanyPlan;

	const hasActiveSubscription = myClub?.subscription?.status === 'ACTIVE';

	const { data: clubsAvailableData, isLoading: isLoadingClubsAvailable } =
		useGetClubsAvailable({
			enabled:
				!isLoadingMyClub &&
				!hasActiveSubscription &&
				!isLoadingMyCompany,
		});

	const { mutate: joinClub, isPending: isJoining } = useJoinClub(
		(checkoutUrl) => {
			window.location.href = checkoutUrl;
		},
	);

	const sortedAvailableClubs = useMemo(() => {
		const clubs = Array.isArray(clubsAvailableData)
			? clubsAvailableData
			: [];
		return [...clubs].sort(
			(a, b) => (b.render_order ?? 0) - (a.render_order ?? 0),
		);
	}, [clubsAvailableData]);

	const pickClubsForDisplay = (clubs: any[]) => {
		const sorted = [...clubs].sort(
			(a, b) => (b.render_order ?? 0) - (a.render_order ?? 0),
		);
		const highlighted = sorted.find((c) => c.render_type === 'HIGHLIGHTED');
		const top = sorted.slice(0, 4);
		if (!highlighted) return top;
		if (top.some((c) => c.id === highlighted.id)) return top;
		if (top.length < 4) return [...top, highlighted];
		return [...top.slice(0, 3), highlighted];
	};

	const companyClubs = useMemo(
		() => (isInCompany ? pickClubsForDisplay(sortedAvailableClubs) : []),
		[isInCompany, sortedAvailableClubs],
	);

	const sortedB2CClubs = useMemo(
		() => (!isInCompany ? pickClubsForDisplay(sortedAvailableClubs) : []),
		[isInCompany, sortedAvailableClubs],
	);

	const handleJoinClub = (clubId: string) => {
		joinClub({
			clubId,
			successUrl: buildCheckoutRedirectUrl('/clubes'),
			cancelUrl: buildCheckoutRedirectUrl('/clubes'),
		});
	};

	if (isLoadingMyClub || hasActiveSubscription) {
		return null;
	}

	const renderClubCard = (
		club: any,
		_index: number,
		isHighlighted: boolean,
	) => {
		const rawPrice = parseFloat(club.price || '0');
		const subsidyPerEmployee = Number(
			activeCompanyPlan?.company_plan?.subsidy_per_employee ?? 0,
		);
		const hasSubsidy = subsidyPerEmployee > 0 && rawPrice > 0;
		const effectiveSubsidy = hasSubsidy
			? Math.min(subsidyPerEmployee, rawPrice)
			: 0;
		const subsidizedPrice = Math.max(0, rawPrice - effectiveSubsidy);

		const formatPrice = (value: number) =>
			value.toLocaleString('pt-BR', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});

		return (
			<div
				key={club.id}
				className={cn(
					'relative flex flex-col justify-between gap-6 rounded-[2.5rem] p-7 pb-8 transition-all duration-300 lg:gap-5 lg:p-6 lg:pb-7',
					isHighlighted
						? 'z-10 bg-zinc-950 text-white shadow-2xl md:scale-105'
						: 'border border-zinc-200 bg-white shadow-sm hover:shadow-2xl hover:shadow-zinc-200/60',
				)}
			>
				{isHighlighted && (
					<div className="absolute -top-4 right-8 rounded-full border border-zinc-800 bg-zinc-900 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white">
						Recomendado
					</div>
				)}

				<div className="space-y-6 text-left">
					<div className="flex items-center gap-4">
						<ClubIcon
							club={club}
							size="lg"
							className={cn(
								isHighlighted &&
									'border border-zinc-800 bg-zinc-900',
							)}
						/>
						<div className="space-y-0.5">
							<h3
								className={cn(
									'text-2xl font-bold leading-tight',
									isHighlighted
										? 'text-white'
										: 'text-zinc-900',
								)}
							>
								{club.name}
							</h3>
							<p className="text-[10px] font-black uppercase tracking-widest text-brand-orange">
								{hasSubsidy ? (
									<>
										<span className="mr-1 line-through opacity-70">
											{club.currency === 'BRL'
												? 'R$'
												: club.currency}{' '}
											{formatPrice(rawPrice)}
										</span>
										<span>
											{club.currency === 'BRL'
												? 'R$'
												: club.currency}{' '}
											{formatPrice(subsidizedPrice)} /mês
										</span>
									</>
								) : (
									<>
										{club.currency === 'BRL'
											? 'R$'
											: club.currency}{' '}
										{formatPrice(rawPrice)} /mês
									</>
								)}
							</p>
						</div>
					</div>

					{club.slogan && (
						<p
							className={cn(
								'text-sm font-medium',
								isHighlighted
									? 'text-zinc-400'
									: 'text-zinc-500',
							)}
						>
							{club.slogan}
						</p>
					)}

					<ul className="space-y-4">
						{sortBenefitsByPriority(club.benefits).map(
							(benefit: any) => (
								<li
									key={benefit.id}
									className="flex items-start gap-3 text-sm font-medium"
								>
									<Check
										size={18}
										className="mt-0.5 shrink-0 text-brand-orange"
									/>
									<span
										className={
											isHighlighted
												? 'text-zinc-400'
												: 'text-zinc-500'
										}
									>
										{benefit.service_substitute_title ||
											benefit.service?.service_name}
									</span>
								</li>
							),
						)}
					</ul>
				</div>

				<Button
					className={cn(
						'w-full justify-center rounded-2xl border-none px-4 py-4 text-[11px] font-black uppercase tracking-[0.18em] transition-all',
						isHighlighted
							? 'hover:bg-brand-orange/92 bg-brand-orange text-white shadow-[0_0_20px_rgba(255,122,86,0.35)]'
							: 'hover:bg-brand-orange/92 border-2 border-brand-orange bg-brand-orange text-white shadow-sm',
						shouldDisableJoinForEmployee &&
							(isHighlighted
								? 'bg-brand-orange/60 text-white hover:bg-brand-orange/60'
								: 'border-brand-orange/40 bg-brand-orange/60 text-white hover:bg-brand-orange/60'),
						isJoining && 'cursor-wait opacity-70',
						shouldDisableJoinForEmployee && 'cursor-not-allowed',
					)}
					text={
						isJoining
							? 'Processando...'
							: shouldDisableJoinForEmployee
							  ? 'Indisponível pela empresa'
							  : 'Assinar agora'
					}
					disabled={isJoining || shouldDisableJoinForEmployee}
					startIcon={
						isJoining ? (
							<Loader2 className="animate-spin" size={16} />
						) : undefined
					}
					onClick={() => setJoinClubToConfirm(club)}
				/>
			</div>
		);
	};

	const clubsToShow = companyClubs.length > 0 ? companyClubs : sortedB2CClubs;

	if (isLoadingMyCompany || isLoadingClubsAvailable) {
		return (
			<div className="flex justify-center py-12">
				<Loader2 className="animate-spin text-brand-orange" size={32} />
			</div>
		);
	}

	if (clubsToShow.length === 0) {
		return null;
	}

	return (
		<>
			<JoinClubConfirmModal
				isOpen={Boolean(joinClubToConfirm)}
				clubName={joinClubToConfirm?.name}
				isPending={isJoining}
				onClose={() => setJoinClubToConfirm(null)}
				onConfirm={() => {
					if (!joinClubToConfirm) return;
					handleJoinClub(joinClubToConfirm.id);
					setJoinClubToConfirm(null);
				}}
			/>
			<section className="w-full space-y-8 pb-16 md:pb-24">
				{companyClubs.length > 0 ? (
					<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
						<div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
							<Building2 size={24} />
						</div>
						<div className="space-y-1">
							<p className="text-xs font-semibold uppercase tracking-wider text-brand-orange">
								Sua empresa
							</p>
							<h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
								{myCompany?.trade_name}
							</h2>
							<p className="text-sm text-zinc-500">
								Planos disponíveis para você através da sua
								empresa.
							</p>
						</div>
					</div>
				) : (
					<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
						<div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
							<Gem size={24} />
						</div>
						<div className="space-y-1">
							<p className="text-xs font-semibold uppercase tracking-wider text-brand-orange">
								Nossos planos
							</p>
							<h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
								A solução certa para o tamanho do seu desafio
							</h2>
							<p className="text-sm text-zinc-500">
								Escolha o cuidado que melhor se adapta às suas
								necessidades.{' '}
								<button
									type="button"
									onClick={() => navigate('/clubes')}
									className={
										userHomeDescriptionAccentLinkClassName
									}
								>
									Ver todos
									<ChevronRight
										size={14}
										className="transition-transform group-hover:translate-x-0.5"
										aria-hidden
									/>
								</button>
							</p>
						</div>
					</div>
				)}
				<div
					className={cn(
						'grid grid-cols-1 items-stretch gap-6 px-2 pt-10 md:grid-cols-2 md:px-3',
						getPlansGridColsClass(clubsToShow.length),
					)}
				>
					{clubsToShow.map((club, index) =>
						renderClubCard(
							club,
							index,
							club.render_type === 'HIGHLIGHTED',
						),
					)}
				</div>
			</section>
		</>
	);
};

export { UserHomePlansSection };
